/**
 * Resource Request Planning Engine
 *
 * Given a ResourceRequest (hours budget + date range + role/skills),
 * this engine calls the backend plan-availability API which:
 *   1. Filters resources by role/skills/location match score
 *   2. Computes daily availability breakdowns from fresh DB data
 *
 * The frontend then builds 3 concrete PlanScenarios using different
 * optimisation strategies from the server-returned data:
 *
 *   1. single-resource  — concentrate all hours on the single best match
 *   2. spread-load      — spread across 2-3 resources to balance capacity
 *   3. fastest-start    — prioritise earliest availability over perfect fit
 *
 * All availability and resource filtering logic lives on the backend.
 */

import { Resource, Booking, WorkingHoursProfile } from '@/types';
import { ResourceRequest, PlanScenario, ProposedBookingSlot, DailyAllocation } from '@/types/resourceRequest';
import { resourceAvailabilityApi, type DailyAvailabilitySlot, type ResourcePlanResult } from '@/services/api';

// ── Helpers ───────────────────────────────────────────────────────────────────

function iso(d: Date) {
  return d.toISOString().split('T')[0];
}

function ym(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(ymStr: string) {
  const [y, m] = ymStr.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}

function startOfMonth(ymStr: string) {
  const [y, m] = ymStr.split('-').map(Number);
  return new Date(y, m - 1, 1);
}

function endOfMonth(ymStr: string) {
  const [y, m] = ymStr.split('-').map(Number);
  return new Date(y, m, 0);
}

function monthsInRange(start: Date, end: Date): string[] {
  const months: string[] = [];
  const cur = new Date(start.getFullYear(), start.getMonth(), 1);
  const endYm = ym(end);
  while (ym(cur) <= endYm) {
    months.push(ym(cur));
    cur.setMonth(cur.getMonth() + 1);
  }
  return months;
}

function slotId() {
  return `slot-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Convert server-returned DailyAvailabilitySlot[] into the distribution shape
 * used by the scenario builders, capping hours to assignedHours.
 *
 * Slots are sorted by available hours descending so the plan picks the largest
 * free windows first, producing fewer and bigger bookings. The resulting
 * allocations are then re-sorted chronologically for display.
 */
function distributeFromServerSlots(
  serverSlots: DailyAvailabilitySlot[],
  assignedHours: number,
): { startDate: Date; endDate: Date; actualHours: number; dailyBreakdown: string[]; dailyAllocations: DailyAllocation[] } | null {
  if (serverSlots.length === 0 || assignedHours <= 0) return null;

  // Sort slots largest-first so we fill the biggest available windows first,
  // minimising the total number of bookings in the plan.
  const sorted = [...serverSlots].sort((a, b) => b.hours - a.hours);

  let remaining = assignedHours;
  const picked: { slot: DailyAvailabilitySlot; roundedHours: number }[] = [];

  for (const slot of sorted) {
    if (remaining <= 0) break;
    const hoursThisDay = Math.min(slot.hours, remaining);
    const roundedHours = Math.round(hoursThisDay * 10) / 10;
    if (roundedHours <= 0) continue;

    picked.push({ slot, roundedHours });
    remaining -= hoursThisDay;
  }

  if (picked.length === 0) return null;

  // Re-sort picked slots chronologically by date then startTime for display
  picked.sort((a, b) => {
    const dateCmp = a.slot.date.localeCompare(b.slot.date);
    if (dateCmp !== 0) return dateCmp;
    return a.slot.startTime.localeCompare(b.slot.startTime);
  });

  const breakdown: string[] = [];
  const allocations: DailyAllocation[] = [];
  let firstDay: Date | null = null;
  let lastDay: Date | null = null;

  for (const { slot, roundedHours } of picked) {
    const dayDate = new Date(slot.date + 'T00:00:00Z');
    if (!firstDay) firstDay = dayDate;
    lastDay = dayDate;

    // Ensure startTime is treated as UTC — append Z if missing
    const startTime = slot.startTime.endsWith('Z') || slot.startTime.includes('+')
      ? slot.startTime
      : slot.startTime + 'Z';
    const startMs = new Date(startTime).getTime();
    const endMs = startMs + roundedHours * 3600000;
    const endTime = new Date(endMs).toISOString();

    breakdown.push(`${dayDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}: ${roundedHours}h`);
    allocations.push({ date: slot.date, hours: roundedHours, startTime, endTime });
  }

  if (!firstDay || !lastDay) return null;

  return {
    startDate: firstDay,
    endDate: lastDay,
    actualHours: assignedHours - Math.max(0, remaining),
    dailyBreakdown: breakdown,
    dailyAllocations: allocations,
  };
}

/**
 * Filter server slots to only those within a specific month range.
 */
function slotsForMonth(slots: DailyAvailabilitySlot[], monthKey: string): DailyAvailabilitySlot[] {
  const mStart = startOfMonth(monthKey);
  const mEnd = endOfMonth(monthKey);
  return slots.filter(s => {
    const d = new Date(s.date + 'T00:00:00');
    return d >= mStart && d <= mEnd;
  });
}

// ── Local fallback: compute ResourcePlanResult[] from frontend mock data ─────
// Used when the backend is unavailable (e.g. demo / frontend-only mode).
// Scores resources by role+skills match, then builds a day-by-day breakdown
// of available hours by subtracting existing bookings from the working profile.

interface LocalPlanInput {
  request: ResourceRequest;
  resources: Resource[];
  existingBookings: Booking[];
  globalWorkingProfile: WorkingHoursProfile;
  requestStart: Date;
  requestEnd: Date;
}

function computeLocalPlanResult(input: LocalPlanInput): ResourcePlanResult[] {
  const { request, resources, existingBookings, globalWorkingProfile, requestStart, requestEnd } = input;

  // Day names mapping (Date.getDay() → profile key)
  const DAY_KEYS: (keyof WorkingHoursProfile)[] = [
    'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
  ];

  // ── Score each resource ──────────────────────────────────────────────────
  function scoreResource(r: Resource): number {
    let score = 40; // base

    // Role match
    const requestRoleLower = request.role.toLowerCase();
    const resourceRoleLower = (r.role ?? '').toLowerCase();
    if (resourceRoleLower === requestRoleLower) {
      score += 35;
    } else if (
      requestRoleLower.split(' ').some(word => resourceRoleLower.includes(word)) ||
      resourceRoleLower.split(' ').some(word => requestRoleLower.includes(word))
    ) {
      score += 15;
    }

    // Skills match
    const resourceSkills = (r.skills ?? []).map(s => s.name.toLowerCase());
    const requestSkills = request.skills.map(s => s.toLowerCase());
    const matched = requestSkills.filter(s => resourceSkills.includes(s)).length;
    if (requestSkills.length > 0) {
      score += Math.round((matched / requestSkills.length) * 20);
    }

    // Location match
    if (request.location && r.location) {
      const reqLoc = request.location.toLowerCase();
      const resLoc = r.location.toLowerCase();
      if (resLoc.includes(reqLoc) || reqLoc.includes(resLoc) || resLoc === 'remote' || reqLoc === 'remote') {
        score += 5;
      }
    } else {
      score += 5; // no preference
    }

    return Math.min(100, score);
  }

  // ── Build daily breakdown for a resource in [requestStart, requestEnd] ──
  function buildDailyBreakdown(r: Resource): DailyAvailabilitySlot[] {
    const profile = r.useCustomWorkingProfile && r.customWorkingProfile
      ? r.customWorkingProfile
      : globalWorkingProfile;

    // Collect busy intervals from existing bookings for this resource
    const resourceBookings = existingBookings.filter(
      b => b.resourceId === r.id || (b.resourceIds ?? []).includes(r.id),
    );

    const slots: DailyAvailabilitySlot[] = [];

    const cur = new Date(requestStart.getFullYear(), requestStart.getMonth(), requestStart.getDate());
    const endDay = new Date(requestEnd.getFullYear(), requestEnd.getMonth(), requestEnd.getDate());

    while (cur <= endDay) {
      const dayKey = DAY_KEYS[cur.getDay()];
      const contractedHours = (profile as unknown as Record<string, number>)[dayKey as string] ?? 0;

      if (contractedHours > 0) {
        const dateStr = iso(cur);
        // Use midnight-to-midnight so preview events fill the full day cell
        // at every zoom level (day / week / month / quarter).
        const dayStart = `${dateStr}T00:00:00Z`;
        const dayEnd = `${dateStr}T23:59:59Z`;

        // Sum booked hours on this day for this resource
        let bookedHours = 0;
        for (const bk of resourceBookings) {
          try {
            const bkStart = new Date(bk.startTime);
            const bkEnd = new Date(bk.endTime);
            const dayMidnight = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate());
            const nextMidnight = new Date(dayMidnight.getTime() + 86400000);
            if (bkEnd > dayMidnight && bkStart < nextMidnight) {
              const overlapStart = Math.max(bkStart.getTime(), dayMidnight.getTime());
              const overlapEnd = Math.min(bkEnd.getTime(), nextMidnight.getTime());
              bookedHours += (overlapEnd - overlapStart) / 3600000;
            }
          } catch { /* skip malformed bookings */ }
        }

        const availableHours = Math.max(0, contractedHours - bookedHours);

        slots.push({
          date: dateStr,
          startTime: dayStart,
          endTime: dayEnd,
          hours: availableHours,
          availableHours,
        });
      }

      cur.setDate(cur.getDate() + 1);
    }

    return slots;
  }

  // ── Build and rank results ─────────────────────────────────────────────
  const results: ResourcePlanResult[] = [];

  for (const r of resources) {
    if (r.id === 'unallocated') continue;

    const score = scoreResource(r);
    // Only include resources with a decent role/skills match (score > 30)
    if (score < 30) continue;

    const dailyBreakdown = buildDailyBreakdown(r);
    const totalAvailable = dailyBreakdown.reduce((sum, s) => sum + s.availableHours, 0);
    if (totalAvailable <= 0) continue;

    // Resource IDs are strings like '1', '17' — parse to number; fall back to index
    const numericId = parseInt(r.id, 10);
    results.push({
      resourceId: isNaN(numericId) ? results.length + 1 : numericId,
      resourceName: r.name,
      resourceRole: r.role ?? '',
      matchScore: score,
      totalAvailableHours: totalAvailable,
      dailyBreakdown,
    });
  }

  // Sort by match score descending
  results.sort((a, b) => b.matchScore - a.matchScore);

  return results;
}

// ── Main planning function ─────────────────────────────────────────────────────

export interface PlannerInput {
  request: ResourceRequest;
  resources: Resource[];
  existingBookings: Booking[];
  globalWorkingProfile: WorkingHoursProfile;
}

export async function generatePlanScenarios(input: PlannerInput): Promise<PlanScenario[]> {
  const { request } = input;

  const requestStart = new Date(request.requestedStartDate);
  let requestEnd = request.requestedEndDate
    ? new Date(request.requestedEndDate)
    : (() => {
        const d = new Date(requestStart);
        d.setMonth(d.getMonth() + 2);
        d.setDate(endOfMonth(ym(d)).getDate());
        return d;
      })();

  // Extend end date to cover all months referenced by monthly hour hints
  if (request.monthlyHourHints) {
    const hintMonths = Object.keys(request.monthlyHourHints).sort();
    if (hintMonths.length > 0) {
      const lastHintMonth = hintMonths[hintMonths.length - 1];
      const lastHintEnd = endOfMonth(lastHintMonth);
      if (lastHintEnd > requestEnd) {
        requestEnd = lastHintEnd;
      }
    }
  }

  const months = monthsInRange(requestStart, requestEnd);
  const totalHours = request.totalHoursRequested;

  // ── Try backend; fall back to local mock computation ────────────────────
  let planResult: ResourcePlanResult[];
  try {
    const response = await resourceAvailabilityApi.planAvailability({
      startDate: requestStart.toISOString(),
      endDate: requestEnd.toISOString(),
      totalHours,
      role: request.role,
      skills: request.skills,
      location: request.location,
    });
    planResult = response.resources;
  } catch (_err) {
    // Backend unavailable — compute availability from local mock data
    console.info('[planAvailability] Backend unavailable — using local mock fallback');
    planResult = computeLocalPlanResult({
      request,
      resources: input.resources,
      existingBookings: input.existingBookings,
      globalWorkingProfile: input.globalWorkingProfile,
      requestStart,
      requestEnd,
    });
  }

  if (planResult.length === 0) return [];

  // Build per-resource per-month availability from server response
  type AvailMap = Record<number, Record<string, number>>;
  const availMap: AvailMap = {};
  type BreakdownCache = Record<number, Record<string, DailyAvailabilitySlot[]>>;
  const breakdownCache: BreakdownCache = {};

  for (const r of planResult) {
    availMap[r.resourceId] = {};
    breakdownCache[r.resourceId] = {};
    for (const monthKey of months) {
      const monthSlots = slotsForMonth(r.dailyBreakdown, monthKey);
      breakdownCache[r.resourceId][monthKey] = monthSlots;
      availMap[r.resourceId][monthKey] = monthSlots.reduce((sum, s) => sum + s.availableHours, 0);
    }
  }

  // ── Helper: build signal reason strings ──────────────────────────────────
  function matchReasons(matchScore: number, resourceRole: string): string[] {
    const reasons: string[] = [];
    if (matchScore >= 85) reasons.push(`Strong overall match (${matchScore}%)`);
    else if (matchScore >= 65) reasons.push(`Good match (${matchScore}%)`);
    else reasons.push(`Partial match (${matchScore}%)`);
    if (resourceRole) reasons.push(`Role: ${resourceRole}`);
    if (request.skills.length > 0) reasons.push(`Skills: ${request.skills.slice(0, 3).join(', ')}`);
    return reasons;
  }

  // ── Strategy 1: Single Resource ──────────────────────────────────────────
  function buildSingleResourceScenario(): PlanScenario | null {
    for (const resource of planResult) {
      const rid = resource.resourceId;
      let remainingHours = totalHours;
      const slots: ProposedBookingSlot[] = [];

      for (const monthKey of months) {
        if (remainingHours <= 0) break;
        const avail = availMap[rid]?.[monthKey] ?? 0;
        if (avail <= 0) continue;

        const hintHours = request.monthlyHourHints?.[monthKey];
        const maxThisMonth = hintHours ?? Math.min(avail, remainingHours);

        const serverSlots = breakdownCache[rid]?.[monthKey] ?? [];
        const distribution = distributeFromServerSlots(serverSlots, maxThisMonth);

        if (distribution) {
          const reasons = matchReasons(resource.matchScore, resource.resourceRole);
          reasons.push(...distribution.dailyBreakdown);

          slots.push({
            id: slotId(),
            resourceId: String(rid),
            resourceName: resource.resourceName,
            resourceRole: resource.resourceRole,
            resourcePhotoUrl: undefined,
            matchScore: resource.matchScore,
            month: monthLabel(monthKey),
            monthKey,
            startDate: iso(distribution.startDate),
            endDate: iso(distribution.endDate),
            hours: distribution.actualHours,
            availableHours: Math.round(avail),
            matchReasons: reasons,
            dailyBreakdown: distribution.dailyAllocations,
          });
          remainingHours -= distribution.actualHours;
        }
      }

      if (slots.length > 0) {
        const covered = totalHours - remainingHours;
        return {
          id: 'sc-single-resource',
          strategy: 'single-resource',
          label: 'Single Resource',
          description: `All ${covered}h assigned to ${resource.resourceName} — simplest coordination`,
          icon: 'User',
          totalHours: covered,
          slots,
          coveragePercent: Math.round((covered / totalHours) * 100),
          resourceCount: 1,
          earliestStart: slots[0].startDate,
          latestEnd: slots[slots.length - 1].endDate,
          pros: [
            'Single point of contact — simplest communication',
            `${resource.resourceName} is the best match for required skills`,
            'Consistent quality — one person owns the deliverable end-to-end',
          ],
          cons: [
            'Single point of failure if the resource becomes unavailable',
            'May overload the resource compared to spreading the work',
          ],
        };
      }
    }
    return null;
  }

  // ── Strategy 2: Spread Load ──────────────────────────────────────────────
  function buildSpreadLoadScenario(): PlanScenario | null {
    const slots: ProposedBookingSlot[] = [];
    let remainingHours = totalHours;

    for (const monthKey of months) {
      if (remainingHours <= 0) break;

      const hintHours = request.monthlyHourHints?.[monthKey];
      let monthBudget = hintHours ?? Math.ceil(remainingHours / Math.max(1, months.length - months.indexOf(monthKey)));
      monthBudget = Math.min(monthBudget, remainingHours);

      // Spread across multiple resources proportionally to their availability
      const resourcesWithAvail = planResult
        .map(r => ({ resource: r, avail: availMap[r.resourceId]?.[monthKey] ?? 0 }))
        .filter(x => x.avail > 0);

      const totalAvailThisMonth = resourcesWithAvail.reduce((s, x) => s + x.avail, 0);
      if (totalAvailThisMonth === 0) continue;

      // Pre-compute shares for each resource, ensuring the full budget is allocated.
      // Use floor for all except the last resource which gets the remainder.
      const shares: number[] = [];
      let allocated = 0;
      for (let i = 0; i < resourcesWithAvail.length; i++) {
        const { avail } = resourcesWithAvail[i];
        const proportion = avail / totalAvailThisMonth;
        if (i === resourcesWithAvail.length - 1) {
          // Last resource gets whatever remains of the budget
          shares.push(Math.min(monthBudget - allocated, avail));
        } else {
          const share = Math.min(Math.ceil(monthBudget * proportion), avail, monthBudget - allocated);
          shares.push(share);
          allocated += share;
        }
      }

      for (let i = 0; i < resourcesWithAvail.length; i++) {
        if (monthBudget <= 0) break;
        const { resource, avail } = resourcesWithAvail[i];
        const rid = resource.resourceId;

        const assignedHours = Math.min(shares[i], monthBudget);
        if (assignedHours <= 0) continue;

        const serverSlots = breakdownCache[rid]?.[monthKey] ?? [];
        const distribution = distributeFromServerSlots(serverSlots, assignedHours);

        if (distribution) {
          const reasons = matchReasons(resource.matchScore, resource.resourceRole);
          reasons.push(...distribution.dailyBreakdown);

          slots.push({
            id: slotId(),
            resourceId: String(rid),
            resourceName: resource.resourceName,
            resourceRole: resource.resourceRole,
            resourcePhotoUrl: undefined,
            matchScore: resource.matchScore,
            month: monthLabel(monthKey),
            monthKey,
            startDate: iso(distribution.startDate),
            endDate: iso(distribution.endDate),
            hours: distribution.actualHours,
            availableHours: Math.round(avail),
            matchReasons: reasons,
            dailyBreakdown: distribution.dailyAllocations,
          });
          monthBudget -= distribution.actualHours;
          remainingHours -= distribution.actualHours;
        }
      }
    }

    if (slots.length === 0) return null;

    const covered = totalHours - remainingHours;
    const uniqueResources = new Set(slots.map(s => s.resourceId)).size;

    return {
      id: 'sc-spread-load',
      strategy: 'spread-load',
      label: 'Spread the Load',
      description: `Hours distributed across ${uniqueResources} resources to balance capacity`,
      icon: 'Users',
      totalHours: covered,
      slots,
      coveragePercent: Math.round((covered / totalHours) * 100),
      resourceCount: uniqueResources,
      earliestStart: slots[0].startDate,
      latestEnd: slots[slots.length - 1].endDate,
      pros: [
        `${uniqueResources} resources share the workload — reduces individual pressure`,
        'Better risk coverage if one resource becomes unavailable',
        'Leverages the most available capacity across the team',
      ],
      cons: [
        'Requires coordination and knowledge sharing between resources',
        'Slightly higher management overhead',
      ],
    };
  }

  // ── Strategy 3: Fastest Start ────────────────────────────────────────────
  function buildFastestStartScenario(): PlanScenario | null {
    const slots: ProposedBookingSlot[] = [];
    let remainingHours = totalHours;

    for (const monthKey of months) {
      if (remainingHours <= 0) break;

      // Sort by available hours this month (highest first) rather than match score
      const byAvailability = planResult
        .map(r => ({ resource: r, avail: availMap[r.resourceId]?.[monthKey] ?? 0 }))
        .filter(x => x.avail > 0)
        .sort((a, b) => b.avail - a.avail);

      if (byAvailability.length === 0) continue;

      const hintHours = request.monthlyHourHints?.[monthKey];
      let monthBudget = hintHours ?? Math.ceil(remainingHours / Math.max(1, months.length - months.indexOf(monthKey)));
      monthBudget = Math.min(monthBudget, remainingHours);

      for (const { resource, avail } of byAvailability) {
        if (monthBudget <= 0) break;
        const rid = resource.resourceId;
        const assignedHours = Math.min(monthBudget, avail);
        if (assignedHours <= 0) continue;

        const serverSlots = breakdownCache[rid]?.[monthKey] ?? [];
        const distribution = distributeFromServerSlots(serverSlots, assignedHours);

        if (distribution) {
          slots.push({
            id: slotId(),
            resourceId: String(rid),
            resourceName: resource.resourceName,
            resourceRole: resource.resourceRole,
            resourcePhotoUrl: undefined,
            matchScore: resource.matchScore,
            month: monthLabel(monthKey),
            monthKey,
            startDate: iso(distribution.startDate),
            endDate: iso(distribution.endDate),
            hours: distribution.actualHours,
            availableHours: Math.round(avail),
            matchReasons: [
              `Highest availability this month (${Math.round(avail)}h free)`,
              `Match score: ${resource.matchScore}%`,
              resource.resourceRole,
              ...distribution.dailyBreakdown,
            ],
            dailyBreakdown: distribution.dailyAllocations,
          });
          monthBudget -= distribution.actualHours;
          remainingHours -= distribution.actualHours;
        }
      }
    }

    if (slots.length === 0) return null;

    const covered = totalHours - remainingHours;
    const uniqueResources = new Set(slots.map(s => s.resourceId)).size;

    return {
      id: 'sc-fastest-start',
      strategy: 'fastest-start',
      label: 'Fastest Start',
      description: 'Maximises availability — gets work started as soon as possible',
      icon: 'Zap',
      totalHours: covered,
      slots,
      coveragePercent: Math.round((covered / totalHours) * 100),
      resourceCount: uniqueResources,
      earliestStart: slots[0].startDate,
      latestEnd: slots[slots.length - 1].endDate,
      pros: [
        'Work begins at the earliest possible date',
        'Maximises use of available capacity right now',
        'Ideal when the project start date is non-negotiable',
      ],
      cons: [
        'May assign resources with slightly lower skill match scores',
        'Availability-first approach may not respect role preferences strictly',
      ],
    };
  }

  const scenarios: PlanScenario[] = [];

  const single = buildSingleResourceScenario();
  if (single) scenarios.push(single);

  const spread = buildSpreadLoadScenario();
  if (spread) scenarios.push(spread);

  const fastest = buildFastestStartScenario();
  if (fastest) scenarios.push(fastest);

  return scenarios;
}


